// apps/web/src/pages/confirm/redirect.tsx — Phase 7 plan 07-06.
//
// Self-service link redirect (D-07). When a guest clicks the
// `/reservierung/<token>` link from their booking confirmation
// email, the request lands on the bare domain (gastio.app). The
// bare-domain server has no tenant context, so this page queries
// the slug for the token via `Query.confirmToken` and
// hard-redirects the browser to the tenant subdomain.
//
// The redirect uses `window.location.replace` so the bare-domain
// entry is dropped from history (Back button stays on the tenant
// subdomain). The fallback for "token not found" renders a small
// "Link nicht mehr gültig" card without redirecting.

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { gqlRequest } from "../../lib/api";

const CONFIRM_TOKEN_QUERY = /* GraphQL */ `
  query ConfirmToken($token: String!) {
    confirmToken(token: $token) {
      tenantSlug
    }
  }
`;

interface ConfirmTokenResponse {
  confirmToken: { tenantSlug: string };
}

export function ConfirmRedirectPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const query = useQuery({
    queryKey: ["confirmToken", token],
    enabled: typeof token === "string",
    queryFn: () =>
      gqlRequest<ConfirmTokenResponse>(CONFIRM_TOKEN_QUERY, { token }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.data?.confirmToken?.tenantSlug) return;
    const slug = query.data.confirmToken.tenantSlug;
    if (typeof window === "undefined") return;
    const baseDomain = window.location.host.replace(/^[^.]+\./, "");
    const target = `${window.location.protocol}//${slug}.${baseDomain}/reservierung/${token}`;
    window.location.replace(target);
  }, [query.data, token]);

  if (query.isError) {
    const err = query.error;
    if (err instanceof ClientError) {
      const codeRaw = err.response?.errors?.[0]?.extensions?.code;
      const code = typeof codeRaw === "string" ? codeRaw : null;
      return (
        <main
          className="min-h-screen bg-bg text-text font-body flex items-center justify-center px-6 py-12"
          data-testid="confirm-invalid"
        >
          <div
            className="card"
            style={{
              borderRadius: 14,
              width: "min(440px, 100%)",
              padding: "28px 24px",
            }}
          >
            <h1 className="font-heading text-[22px] font-semibold mb-2">
              Link nicht mehr gültig
            </h1>
            <p className="text-sm text-text/70">
              Dieser Link ist abgelaufen oder wurde bereits verwendet. Bitte
              kontaktieren Sie das Restaurant, um Ihre Reservierung zu
              verwalten.
            </p>
            {code ? (
              <p className="text-[11px] text-text/50 mt-3">
                Fehlercode: {code}
              </p>
            ) : null}
          </div>
        </main>
      );
    }
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex items-center justify-center px-6"
      data-testid="confirm-redirect"
    >
      <p className="text-sm text-text/70">Redirecting…</p>
    </main>
  );
}
